import { useEffect, useMemo, useState } from 'react';

import { hasIntrinsicSize, ImageSize, NO_IMAGE_SIZE, scaleToWidth } from './useImageSize.common';

const useImageSize = (
  uri: string,
  deviceWidth: number,
  maxWidth: number,
  intrinsicSize?: ImageSize
): ImageSize => {
  const isKnown = hasIntrinsicSize(intrinsicSize);
  const [measuredSize, setMeasuredSize] = useState<ImageSize>(NO_IMAGE_SIZE);

  useEffect(() => {
    // Downloading the whole photo to read `naturalWidth` is the fallback for a
    // photo that arrives without dimensions: a local file picked for upload, and
    // the galleries whose images carry none — message attachments and the sitter
    // relationship gallery.
    if (isKnown || !uri) return;

    const img = new Image();
    img.onload = () => {
      setMeasuredSize(scaleToWidth(img.naturalWidth, img.naturalHeight, deviceWidth, maxWidth));
    };
    img.src = uri;
  }, [isKnown, uri, deviceWidth, maxWidth]);

  return useMemo(() => {
    if (!intrinsicSize || !isKnown) return measuredSize;

    return scaleToWidth(intrinsicSize.width, intrinsicSize.height, deviceWidth, maxWidth);
  }, [intrinsicSize, isKnown, measuredSize, deviceWidth, maxWidth]);
};

export default useImageSize;
